import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, Chrome, Phone } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_a3314fd0-818c-4089-8369-a8d30cc8683e/artifacts/o88f67ha_image.png';

const AuthPage = () => {
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '', newsletterOptIn: true, inventoryOptIn: true });

  const from = location.state?.from?.pathname || '/dashboard';
  const params = new URLSearchParams(location.search);
  const defaultTab = params.get('mode') === 'register' ? 'register' : 'login';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (registerForm.phone.replace(/\D/g, '').length < 7) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      await register(registerForm.name, registerForm.email, registerForm.password, registerForm.phone, {
        newsletterOptIn: registerForm.newsletterOptIn,
        inventoryOptIn: registerForm.inventoryOptIn,
      });
      toast.success('Account created successfully!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-16 px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="Genora Peptides" className="h-14 mx-auto mb-4" />
          </Link>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Welcome</h1>
          <p className="text-slate-600 mt-2">Sign in to access your account and orders</p>
        </div>

        <Card className="border-slate-100">
          <CardContent className="pt-6">
            <Tabs defaultValue={defaultTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register">Create Account</TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="researcher@lab.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="login-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="login-password"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white h-11"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Dr. Jane Smith"
                        value={registerForm.name}
                        onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="register-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="researcher@lab.com"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="register-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-phone">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="register-phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={registerForm.phone}
                        onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="register-phone"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="register-password"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-confirm">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="register-confirm"
                        type="password"
                        placeholder="••••••••"
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="register-confirm"
                      />
                    </div>
                  </div>

                  <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="register-email-prefs">
                    <p className="text-sm font-medium text-slate-700">Stay in the loop (optional)</p>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={registerForm.inventoryOptIn}
                        onCheckedChange={(v) => setRegisterForm({ ...registerForm, inventoryOptIn: !!v })}
                        className="mt-0.5"
                        data-testid="register-inventory-optin"
                      />
                      <span className="text-sm text-slate-600 leading-snug">
                        <span className="font-medium text-slate-700">Inventory &amp; restock alerts</span> — email me when products are back in stock or new items arrive.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={registerForm.newsletterOptIn}
                        onCheckedChange={(v) => setRegisterForm({ ...registerForm, newsletterOptIn: !!v })}
                        className="mt-0.5"
                        data-testid="register-newsletter-optin"
                      />
                      <span className="text-sm text-slate-600 leading-snug">
                        <span className="font-medium text-slate-700">Newsletter &amp; research updates</span> — occasional articles, guides and member offers.
                      </span>
                    </label>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white h-11"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>

                  {/* Preference disclosure — CAN-SPAM / GDPR friendly. */}
                  <p
                    className="text-xs text-slate-500 leading-snug"
                    data-testid="newsletter-opt-in-disclosure"
                  >
                    You control what you receive — choose above, and change it anytime via the
                    link in any email or in your{' '}
                    <a
                      href="/dashboard?tab=preferences"
                      className="underline text-teal-700 hover:text-teal-800"
                    >
                      account preferences
                    </a>.
                  </p>
                </form>
              </TabsContent>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Or continue with</span>
                </div>
              </div>

              {/* Google Login */}
              <Button 
                type="button"
                variant="outline" 
                className="w-full h-11"
                onClick={loginWithGoogle}
                data-testid="google-login"
              >
                <Chrome className="mr-2 h-5 w-5 text-[#4285F4]" />
                Continue with Google
              </Button>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          By signing up, you agree to our{' '}
          <Link to="/terms" className="text-teal-700 hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy-policy" className="text-teal-700 hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
